/**
 * Check-In service-tier + expedite-tier registry.
 *
 * Drives the two-picker UX on the per-draft-job form:
 *   Service Tier  ◉ Repair  ○ Data Recovery
 *   Urgency       ◉ Standard  ○ Same Day  ○ Next Day  ○ Express Emergency
 *
 * Each picker contributes a product code to the auto-composed preliminary
 * charges. Prices live in RS and are looked up at runtime via
 * `useProductCatalog`. SKUs configurable here so admin changes don't need
 * code edits to consumers.
 */

import type { CheckInDeviceType } from '@techgitter/contracts';

export interface CheckInServiceTier {
  id: 'repair' | 'data_recovery' | 'new' | 'setup' | 'build';
  label: string;
  /** Guidance shown under the label in the Service Tier picker. */
  description?: string;
  /** Per-device diagnostic SKU auto-added to preliminary charges in
   *  diagnose-and-quote mode. `null` for tiers that don't carry an auto
   *  diagnostic (New / Setup / Build) — the receptionist adds any charges
   *  manually (or the job is pre-paid in full). */
  productCodeByDeviceType: Record<CheckInDeviceType, string> | null;
  /** Flat pre-paid service SKU auto-added to preliminary charges whenever this
   *  tier is selected, regardless of service mode (e.g. Setup → a fixed
   *  computer-setup service fee). Omitted for tiers with no upfront service. */
  prepaidServiceSku?: string | null;
}

/**
 * Per-device transaction mode. Defined in the contracts package as it's
 * persisted to Dataverse (cr001_service_mode). Re-exported here so the
 * Check-In form components have a single import surface for the
 * service-tier + expedite-tier + service-mode types.
 */
export type { CheckInServiceMode } from '@techgitter/contracts';

export interface CheckInExpediteTier {
  id: 'standard' | 'same_day' | 'next_day' | 'emergency';
  label: string;
  /** null = no extra line item (the standard / non-urgent default) */
  productCode: string | null;
  description?: string;
}

export const CHECK_IN_SERVICE_TIERS: CheckInServiceTier[] = [
  {
    id: 'repair',
    label: 'Repair',
    description: 'Standard repair service (diagnostic fee by device type)',
    // The diagnostic fee follows what the device is (Steve, 2026-10-09):
    // fixed machines (desktops, all-in-ones) carry the larger fee, phones and
    // tablets their own, and bare drives are a data-recovery diagnosis. A
    // code missing from the catalog composes as a $0 line, which the charges
    // editor flags rather than letting it reach the invoice quietly.
    productCodeByDeviceType: {
      laptop:          'RCS.Diagnostics.Laptop',
      desktop:         'RCS.Diagnostics.Fixed',
      all_in_one:      'RCS.Diagnostics.Fixed',
      phone:           'RCS.Diagnostics.Handheld',
      tablet:          'RCS.Diagnostics.Handheld',
      hard_drive:      'RCS.Diagnostics.DataRecovery',
      usb_flash_drive: 'RCS.Diagnostics.DataRecovery',
      console:         'RCS.Diagnostics.Console',
      other:           'RCS.Diagnostics.Other',
    },
  },
  {
    id: 'new',
    label: 'New',
    description: 'Quote for a new computer based off the existing computer (no additional charge)',
    productCodeByDeviceType: null,
  },
  {
    id: 'setup',
    label: 'Setup',
    description: 'Standard setup service for a new computer (RCS.Win.ComputerSetupService, pre-paid in full)',
    productCodeByDeviceType: null,
    prepaidServiceSku: 'RCS.Win.ComputerSetupService',
  },
  {
    id: 'build',
    label: 'Build',
    description: "Build a computer using the customer's parts (RCS.Diagnostics)",
    productCodeByDeviceType: {
      laptop:          'RCS.Diagnostics',
      desktop:         'RCS.Diagnostics',
      all_in_one:      'RCS.Diagnostics',
      phone:           'RCS.Diagnostics',
      tablet:          'RCS.Diagnostics',
      hard_drive:      'RCS.Diagnostics',
      usb_flash_drive: 'RCS.Diagnostics',
      console:         'RCS.Diagnostics',
      other:           'RCS.Diagnostics',
    },
  },
  {
    id: 'data_recovery',
    label: 'Data Recovery',
    description: 'Data recovery from a computer, hard drive, USB device, SD card, etc. (RCS.Diagnostics.DataRecovery)',
    productCodeByDeviceType: {
      laptop:          'RCS.Diagnostics.DataRecovery',
      desktop:         'RCS.Diagnostics.DataRecovery',
      all_in_one:      'RCS.Diagnostics.DataRecovery',
      phone:           'RCS.Diagnostics.DataRecovery',
      tablet:          'RCS.Diagnostics.DataRecovery',
      hard_drive:      'RCS.Diagnostics.DataRecovery',
      usb_flash_drive: 'RCS.Diagnostics.DataRecovery',
      console:         'RCS.Diagnostics.DataRecovery',
      other:           'RCS.Diagnostics.DataRecovery',
    },
  },
];

export const CHECK_IN_EXPEDITE_TIERS: CheckInExpediteTier[] = [
  {
    id: 'standard',
    label: 'Standard',
    productCode: null,
    description: '1-2 business day diagnostics',
  },
  {
    id: 'same_day',
    label: 'Same Day',
    productCode: 'RCS.Expedite.Express',
    description: 'Same-day diagnostics • Non-refundable. Same-day resolution not guaranteed.',
  },
  {
    id: 'next_day',
    label: 'Next Day (drop off before 11am)',
    productCode: 'RCS.Expedite.NextDay',
    description: 'Next-day drop off (before 11am) & diagnostics • Non-refundable.',
  },
  {
    id: 'emergency',
    label: 'Express Emergency (immediate start)',
    productCode: 'RCS.Expedite.Emergency',
    description: 'Emergency diagnostics • Immediate start • Non-refundable.',
  },
];

export function getServiceTier(id: CheckInServiceTier['id']): CheckInServiceTier {
  return CHECK_IN_SERVICE_TIERS.find(t => t.id === id) ?? CHECK_IN_SERVICE_TIERS[0];
}

export function getExpediteTier(id: CheckInExpediteTier['id']): CheckInExpediteTier {
  return CHECK_IN_EXPEDITE_TIERS.find(t => t.id === id) ?? CHECK_IN_EXPEDITE_TIERS[0];
}
